import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../../lib/api'
import { useAuth } from '../../contexts/AuthContext'
import { getOnboardingState } from '../../hooks/useOnboardingState'

export function AuthCallback() {
  const navigate = useNavigate()
  const { verifyToken } = useAuth()
  // Prevent double execution in React StrictMode (OAuth codes can only be used once)
  const hasRun = useRef(false)

  useEffect(() => {
    // Skip if already run (StrictMode double-invocation protection)
    if (hasRun.current) return
    hasRun.current = true

    const handleAuthCallback = async () => {
      try {
        // Get the authorization code from URL parameters
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')
        const error = urlParams.get('error')

        if (error) {
          console.error('OAuth error:', error)
          navigate('/login?error=oauth_error')
          return
        }

        if (!code) {
          console.error('No authorization code received')
          navigate('/login?error=no_code')
          return
        }

        // Exchange code for token
        const redirectUri = `${window.location.origin}/auth/callback`
        
        // Get referral code if stored before OAuth redirect
        const referralCode = sessionStorage.getItem('referral_code')
        if (referralCode) {
          sessionStorage.removeItem('referral_code')
          console.log('Using referral code:', referralCode)
        }
        
        // Get consent data stored before OAuth redirect
        const consentDataStr = sessionStorage.getItem('auth_consent')
        let consentData: { accepted_terms: boolean; accepted_marketing: boolean } | undefined
        if (consentDataStr) {
          try {
            consentData = JSON.parse(consentDataStr)
            sessionStorage.removeItem('auth_consent')
          } catch (e) {
            console.error('Failed to parse consent data:', e)
          }
        }
        
        let authData
        try {
          authData = await apiClient.authenticateWithGoogle(
            code, 
            redirectUri, 
            referralCode || undefined,
            consentData
          )
        } catch (authError: unknown) {
          // Check if this is a "new user needs to sign up" error
          const err = authError as { message?: string }
          if (err.message?.includes('new_user_needs_signup')) {
            navigate('/login?error=new_user_needs_signup')
            return
          }
          throw authError
        }

        if (authData.success) {
          // Verify the token to get user data
          await verifyToken()
          
          // Transfer onboarding data from localStorage to user account
          const onboardingData = getOnboardingState();
          if (onboardingData && (onboardingData.userType || onboardingData.targetLanguage)) {
            try {
              await apiClient.preferences.updateUserPreferences({
                user_type: onboardingData.userType || undefined,
                native_language: onboardingData.nativeLanguage || undefined,
                preferred_study_language: onboardingData.targetLanguage || undefined,
                preferred_cefr_level: onboardingData.cefrLevel || undefined,
                onboarding_completed: onboardingData.completed || undefined,
              });
              
              // Keep onboarding state in localStorage to remember completion across logout/login
              // This prevents the onboarding from reappearing for the same browser session
              console.log('Onboarding data transferred to user account (localStorage preserved)');
            } catch (error) {
              console.error('Failed to transfer onboarding data:', error);
              // Don't fail the login if onboarding transfer fails
            }
          }
          
          // Navigate to intended path if available, otherwise home
          const stored = sessionStorage.getItem('post_login_redirect')
          if (stored) {
            sessionStorage.removeItem('post_login_redirect')
            navigate(stored, { replace: true })
          } else {
            navigate('/', { replace: true })
          }
        } else {
          navigate('/login?error=auth_failed')
        }
      } catch (error) {
        console.error('Authentication callback error:', error)
        navigate('/login?error=auth_error')
      }
    }

    handleAuthCallback()
  }, [navigate, verifyToken])

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-foreground mb-2">
          Completing authentication...
        </h2>
        <p className="text-muted-foreground">
          Please wait while we finish setting up your account.
        </p>
      </div>
    </div>
  )
}
